import {
 LeadTimeLineEmptyMessageIcon,
 LeadTimeLineEmptyNotesIcon,
 LeadTimelineCallIcon,
} from 'components/icons'
import { FC } from 'react'
import ActivityEmptyState, {
 ACTION_ICONS,
 EmptyStateAction,
 IntegrationAction,
 useLeadActivityActions,
 WebsiteLinkDropdown,
} from './ActivityEmptyState'

// AR0569 - default messages for each lead timeline tab

const NO_PHONE = 'No phone number for this lead'

// First failing condition's reason, or undefined when the action is usable
const disabledReason = (
 checks: [failed: boolean, reason: string][],
): string | undefined => checks.find(([failed]) => failed)?.[1]

export const AllActivityEmptyState: FC<{ onAddNote: () => void }> = ({
 onAddNote,
}) => {
 const { leadName, canSms, canCall, hasPhone, sendEmail, sendSms, call } =
  useLeadActivityActions()
 // Without Twilio show the button greyed out with the reason
 const smsDisabled = disabledReason([
  [!canSms, 'Connect Twilio to send SMS'],
  [!hasPhone, NO_PHONE],
 ])
 const callDisabled = disabledReason([
  [!canCall, 'Connect Twilio to make calls'],
  [!hasPhone, NO_PHONE],
 ])

 return (
  <ActivityEmptyState
   icon={<i className="fas fa-history no-data-image" />}
   headline="No activity yet"
   body={
    <>
     Get started with {leadName()} — reach out or jot down a note, and
     it&apos;ll all show up here.
    </>
   }
   actions={
    <>
     <EmptyStateAction
      label="Send Email"
      icon={ACTION_ICONS.email}
      onClick={() => sendEmail()}
     />
     <EmptyStateAction
      label="Send SMS"
      icon={ACTION_ICONS.sms}
      onClick={() => sendSms()}
      disabledReason={smsDisabled}
     />
     <EmptyStateAction
      label="Call Now"
      icon={ACTION_ICONS.call}
      onClick={call}
      disabledReason={callDisabled}
     />
     <EmptyStateAction
      label="Add Note"
      icon={ACTION_ICONS.note}
      onClick={onAddNote}
     />
     <WebsiteLinkDropdown />
    </>
   }
  />
 )
}

export const SmsEmptyState: FC = () => {
 const { leadName, canSms, hasPhone, sendSms } = useLeadActivityActions()
 const icon = (
  <span className="no-data-image">
   <LeadTimeLineEmptyMessageIcon />
  </span>
 )

 if (!canSms) {
  return (
   <ActivityEmptyState
    icon={icon}
    headline="SMS messaging isn't set up yet"
    body="Connect Twilio to text leads directly from their profile."
    actions={<IntegrationAction label="Set Up SMS Integration" />}
   />
  )
 }
 return (
  <ActivityEmptyState
   icon={icon}
   headline="No SMS conversations yet"
   body={
    <>
     You haven&apos;t texted {leadName()} yet. A quick SMS is often the fastest
     way to get a response.
    </>
   }
   actions={
    <EmptyStateAction
     label="Send SMS"
     icon={ACTION_ICONS.sms}
     onClick={() => sendSms()}
     disabledReason={!hasPhone ? NO_PHONE : undefined}
    />
   }
  />
 )
}

export const EmailEmptyState: FC = () => {
 const { leadName, hasEmail, sendEmail } = useLeadActivityActions()
 return (
  <ActivityEmptyState
   icon={<i className="fal fa-envelope-open no-data-image" />}
   headline="No email activity yet"
   body={
    <>
     You haven&apos;t emailed {leadName()} yet. First impressions count — a
     quick note goes a long way.
    </>
   }
   actions={
    <EmptyStateAction
     label="Send Email"
     icon={ACTION_ICONS.email}
     onClick={() => sendEmail()}
     disabledReason={!hasEmail ? 'No email address for this lead' : undefined}
    />
   }
  />
 )
}

export const NotesEmptyState: FC<{ onAddNote: () => void }> = ({
 onAddNote,
}) => {
 const { leadName } = useLeadActivityActions()
 return (
  <ActivityEmptyState
   icon={
    <span className="no-data-image">
     <LeadTimeLineEmptyNotesIcon />
    </span>
   }
   headline="No notes yet"
   body={
    <>
     Keep track of what matters with {leadName()} — property preferences,
     objections, follow-up reminders.
    </>
   }
   actions={
    <EmptyStateAction
     label="Add Note"
     icon={ACTION_ICONS.note}
     onClick={onAddNote}
    />
   }
  />
 )
}

export const CallLogEmptyState: FC = () => {
 const { leadName, canCall, hasPhone, call } = useLeadActivityActions()
 const icon = (
  <span className="no-data-image">
   <LeadTimelineCallIcon />
  </span>
 )

 if (!canCall) {
  return (
   <ActivityEmptyState
    icon={icon}
    headline="Calling isn't set up yet"
    body="Connect Twilio to make and log calls directly from lead profiles."
    actions={<IntegrationAction label="Set Up Twilio Integration" />}
   />
  )
 }
 return (
  <ActivityEmptyState
   icon={icon}
   headline="No call history yet"
   body={
    <>Give {leadName()} a call — voice still closes deals faster than text.</>
   }
   actions={
    <EmptyStateAction
     label="Call Now"
     icon={ACTION_ICONS.call}
     onClick={call}
     disabledReason={!hasPhone ? NO_PHONE : undefined}
    />
   }
  />
 )
}

export const WebActivityEmptyState: FC = () => {
 const {
  leadName,
  canSms,
  hasPhone,
  websiteUrl,
  sendWebsiteEmail,
  sendWebsiteSms,
  copyWebsiteLink,
 } = useLeadActivityActions()
 const noSite = !websiteUrl ? 'Add your website in your profile' : undefined

 return (
  <ActivityEmptyState
   icon={<i className="fas fa-clipboard no-data-image" />}
   headline="No web activity yet"
   body={
    <>
     {leadName(true)} hasn&apos;t visited your website. Share your site so you
     can start tracking their browsing, searches, and saved listings.
    </>
   }
   actions={
    <>
     <EmptyStateAction
      label="Send via Email"
      icon={ACTION_ICONS.email}
      onClick={sendWebsiteEmail}
      disabledReason={noSite}
     />
     {/* SMS only when a CRM number / Twilio is available; hidden otherwise */}
     {canSms && (
      <EmptyStateAction
       label="Send via SMS"
       icon={ACTION_ICONS.sms}
       onClick={sendWebsiteSms}
       disabledReason={noSite ?? (!hasPhone ? NO_PHONE : undefined)}
      />
     )}
     <EmptyStateAction
      label="Copy Link"
      icon={ACTION_ICONS.copy}
      onClick={copyWebsiteLink}
      disabledReason={noSite}
     />
    </>
   }
  />
 )
}
